import React from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';

import { PaymentMethodSelection } from '~/components/checkout/checkout-form';
import { Form } from '~/components/form';
import { useFlashMessage } from '~/features/flash-messages/flash-message-actions';
import {
  confirmPayment,
  confirmSetup,
  handleValidation,
} from '~/features/stripe';
import { captureException } from '~/utils/exception-tracking';

const PaymentForm = ({
  children,
  initialValues,
  onSubmit,
  savedPaymentMethod,
  paymentIntent,
  cartUuid,
  redirectUrl,
}) => {
  // Get a reference to Stripe and Elements using hooks.
  // 'elements' handles form input values directly
  const stripe = useStripe();
  const elements = useElements();
  const { showFlashMessage } = useFlashMessage();

  const handleSubmit = async (formValues) => {
    if (elements === null) {
      return;
    }

    // try {
    // await api.cart.paymentValidation(
    //   cartUuid,
    //   paymentIntent.provider_charge_id
    // );

    if (formValues.paymentMethod === PaymentMethodSelection.EXISTING) {
      let res = {};
      if (paymentIntent.intent_type === 'setup') {
        // this is if a cart total is $0, we need to still accept
        // a card to keep on file and store card as part of SetupIntent
        await handleValidation(elements, showFlashMessage);
        res = await confirmSetup(
          stripe,
          elements,
          paymentIntent.client_secret,
          {
            payment_method: savedPaymentMethod.id,
            return_url: redirectUrl,
          }
        );
      } else {
        res = await confirmPayment(
          stripe,
          elements,
          paymentIntent.client_secret,
          {
            payment_method: savedPaymentMethod.id,
            return_url: redirectUrl,
          }
        );
      }

      const paymentMethod =
        res.paymentIntent?.payment_method || res.setupIntent?.payment_method;

      if (res.error) {
        // since the parseStripeTokenError only responds with error messaging for input fields
        // with a user using a saved payment method, this fails silently, so we are displaying
        // a message if there is an error for saved payment methods when we can't connect to stripe.
        if (res.error.type === 'api_connection_error') {
          captureException(new Error('Error connecting to Stripe.'), {
            extras: {
              error: res.error,
            },
          });
          showFlashMessage(
            'Sorry, something went wrong. We are unable to complete your order at this time. Please try again later.',
            'error'
          );
          return;
        } else {
          captureException(new Error('Error connecting to Stripe.'), {
            extras: {
              error: res.error.message,
            },
          });

          return showFlashMessage(res.error.message, 'error');
        }
      } else {
        return onSubmit(paymentMethod);
      }
    }

    let res = {};
    if (paymentIntent.intent_type === 'setup') {
      // this is if a cart total is $0, we need to still accept
      // a card to keep on file and store card as part of SetupIntent
      await handleValidation(elements, showFlashMessage);
      res = await confirmSetup(stripe, elements, paymentIntent.client_secret, {
        payment_method_data: {
          billing_details: {
            address: {
              country: 'US',
            },
          },
        },
        return_url: redirectUrl,
      });
    } else {
      await handleValidation(elements, showFlashMessage);
      res = await confirmPayment(
        stripe,
        elements,
        paymentIntent.client_secret,
        {
          payment_method_data: {
            billing_details: {
              address: {
                country: 'US',
              },
            },
          },
          return_url: redirectUrl,
        }
      );
    }

    const paymentMethod =
      res.paymentIntent?.payment_method || res.setupIntent?.payment_method;

    if (res.error) {
      return showFlashMessage(res.error.message, 'error');
    } else {
      return onSubmit(paymentMethod);
    }
    // } catch (err) {
    //   captureException(
    //     new Error('Cart and PaymentIntent totals do not match.'),
    //     {
    //       extras: {
    //         error: JSON.stringify(err.error),
    //       },
    //     }
    //   );
    //   await queryClient.invalidateQueries(['cart']);
    //   showFlashMessage(
    //     'Uh oh! It looks like your cart contents may have changed. Please review and try again.'
    //   );
    //   navigate(cartRoute());
    // }
  };

  return (
    <Form onSubmit={handleSubmit} initialValues={initialValues}>
      {children}
    </Form>
  );
};

export default PaymentForm;
